import React, { useState, useEffect } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { adminApi, FeatureDefinition, FeatureCategory } from '../services/api';

interface FeatureSelectorProps {
  selectedFeatures: string[];
  onChange: (features: string[]) => void;
  disabled?: boolean;
}

const FeatureSelector: React.FC<FeatureSelectorProps> = ({
  selectedFeatures,
  onChange,
  disabled = false,
}) => {
  const [features, setFeatures] = useState<FeatureDefinition[]>([]);
  const [byCategory, setByCategory] = useState<Record<FeatureCategory, FeatureDefinition[]>>({} as any);
  const [proprietaryFeatures, setProprietaryFeatures] = useState<FeatureDefinition[]>([]);
  const [categoryLabels, setCategoryLabels] = useState<Record<FeatureCategory, string>>({} as any);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [invalidFeatures, setInvalidFeatures] = useState<string[]>([]);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        setLoading(true);
        const response = await adminApi.listFeatures();
        setFeatures(response.data.features);
        setByCategory(response.data.byCategory);
        setProprietaryFeatures(response.data.byVisibility.proprietary);
        setCategoryLabels(response.data.categoryLabels);

        // Auto-clean deprecated/invalid features from selection
        const allKnownIds = new Set(response.data.features.map((f: FeatureDefinition) => String(f.id)));
        const deprecated = selectedFeatures.filter((id) => !allKnownIds.has(id));
        if (deprecated.length > 0) {
          setInvalidFeatures(deprecated);
          // Strip deprecated features so they won't be saved back
          onChange(selectedFeatures.filter((id) => allKnownIds.has(id)));
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load features');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, []);

  const handleFeatureToggle = (featureId: string) => {
    if (selectedFeatures.includes(featureId)) {
      onChange(selectedFeatures.filter((id) => id !== featureId));
    } else {
      onChange([...selectedFeatures, featureId]);
    }
  };


  const handleSelectAllInCategory = (category: FeatureCategory) => {
    const categoryIds = (byCategory[category] || []).map((f) => String(f.id));
    const otherIds = selectedFeatures.filter(
      (id) => !categoryIds.includes(id)
    );
    onChange([...otherIds, ...categoryIds]);
  };

  const handleDeselectAllInCategory = (category: FeatureCategory) => {
    const categoryIds = (byCategory[category] || []).map((f) => String(f.id));
    onChange(selectedFeatures.filter((id) => !categoryIds.includes(id)));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Group categories by visibility
  const generalCategories = Object.entries(byCategory).filter(([_, features]) =>
    features.some((f) => f.visibility === 'GENERAL')
  );
  const proprietaryCategories = Object.entries(byCategory).filter(([_, features]) =>
    features.some((f) => f.visibility === 'PROPRIETARY')
  );

  return (
    <Box>
      {/* Warning for deprecated features that were auto-removed */}
      {invalidFeatures.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The following deprecated feature(s) were removed from the selection: <strong>{invalidFeatures.join(', ')}</strong>. 
          They will be cleaned up when you save.
        </Alert>
      )}

      {/* General Features Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>
            General Features
          </Typography>
        </Box>

        {generalCategories.map(([category, categoryFeatures]) => {
          const categoryKey = category as FeatureCategory;
          const generalFeaturesInCategory = categoryFeatures.filter(
            (f) => f.visibility === 'GENERAL'
          );

          if (generalFeaturesInCategory.length === 0) return null;

          const selectedInCategory = generalFeaturesInCategory.filter((f) =>
            selectedFeatures.includes(f.id)
          ).length;

          return (
            <Accordion key={category} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {categoryLabels[categoryKey] || category}
                  </Typography>
                  <Chip
                    label={`${selectedInCategory}/${generalFeaturesInCategory.length}`}
                    size="small"
                    color={selectedInCategory === generalFeaturesInCategory.length ? 'success' : 'default'}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedInCategory === generalFeaturesInCategory.length) {
                        handleDeselectAllInCategory(categoryKey);
                      } else {
                        handleSelectAllInCategory(categoryKey);
                      }
                    }}
                    disabled={disabled}
                  >
                    {selectedInCategory === generalFeaturesInCategory.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {generalFeaturesInCategory.map((feature) => (
                    <Box key={String(feature.id)} sx={{ pl: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFeatures.includes(String(feature.id))}
                            onChange={() => handleFeatureToggle(String(feature.id))}
                            disabled={disabled}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {feature.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {feature.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Proprietary Features Section */}
      {proprietaryFeatures.length > 0 && (
        <Box>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>
              Proprietary Features
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Organization-specific features (disabled by default)
            </Typography>
          </Box>

          {proprietaryCategories.map(([category, categoryFeatures]) => {
            const categoryKey = category as FeatureCategory;
            const proprietaryFeaturesInCategory = categoryFeatures.filter(
              (f) => f.visibility === 'PROPRIETARY'
            );

            if (proprietaryFeaturesInCategory.length === 0) return null;

            const selectedInCategory = proprietaryFeaturesInCategory.filter((f) =>
              selectedFeatures.includes(f.id)
            ).length;

            return (
              <Accordion key={category}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {categoryLabels[categoryKey] || category}
                    </Typography>
                    <Chip
                      label={`${selectedInCategory}/${proprietaryFeaturesInCategory.length}`}
                      size="small"
                      color={selectedInCategory > 0 ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {proprietaryFeaturesInCategory.map((feature) => (
                      <Box key={String(feature.id)} sx={{ pl: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedFeatures.includes(String(feature.id))}
                              onChange={() => handleFeatureToggle(String(feature.id))}
                              disabled={disabled}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {feature.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {feature.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{selectedFeatures.length}</strong> of <strong>{features.length}</strong> features selected
        </Typography>
      </Box>
    </Box>
  );
};

export default FeatureSelector;
